import * as THREE from "three";

import type { CapabilityMessage, CapabilityResult } from "../api";
import { STEPS_PER_FRAME, TIMESTEP, protocol } from "../protocol";
import { getMujoco } from "./mujoco";

const WARMUP_MS = 1_800;
const DISCARD_MS = 300;

function rendererName(gl: WebGL2RenderingContext): string {
  const extension = gl.getExtension("WEBGL_debug_renderer_info");
  return extension
    ? String(gl.getParameter(extension.UNMASKED_RENDERER_WEBGL))
    : String(gl.getParameter(gl.RENDERER));
}

const MIN_WIDTH = 900;
// No frame-rate floor. Nothing breaks below any particular rate: the simulation simply runs
// slower than real time, dragging still works, and the unlock counters are wall-clock. The
// risk is judgement quality, not function, so the rate is recorded, warned about, and left
// for analysis to filter -- see report.json["capability"].

/**
 * Only one thing is a hard requirement: WebGL2, without which there is no viewer and three
 * of the six dimensions cannot be answered at all. Everything else degrades rather than
 * breaks, so it is reported as an advisory and the participant decides.
 *
 * The exception is a floor on measured frame rate, kept at exactly the server's own bar.
 * Passing a participant the client is happy with and then having the API reject them with a
 * generic "设备检查未通过" is worse than saying so up front.
 */
function pointerAdvisory(): CapabilityMessage | null {
  // Gate on having a precise pointer, not on the absence of a touchscreen: the old rule was
  // `maxTouchPoints === 0`, which rejects every touchscreen laptop although a trackpad drives
  // the study fine. Headless Chrome reports no pointer capabilities at all, so the two are
  // OR-ed: anything the old rule admitted still passes, and the acceptance suite still runs.
  const fine = window.matchMedia("(any-pointer: fine)").matches;
  const touchPoints = navigator.maxTouchPoints;
  if (fine || touchPoints === 0) return null;
  return { template: "未检测到鼠标或触控板（触点数 {0}）。触屏拖动部件会比较吃力，建议改用桌面电脑。",
    values: [touchPoints] };
}

function widthAdvisory(): CapabilityMessage | null {
  const width = window.innerWidth;
  if (width >= MIN_WIDTH) return null;
  return { template: "浏览器窗口只有 {0}px 宽，建议至少 {1}px：最大化窗口，或按住 Ctrl/⌘ 再按 0 把缩放恢复到 100%，否则左右两栏会挤在一起。",
    values: [width, MIN_WIDTH] };
}

export async function runCapabilityGate(): Promise<CapabilityResult> {
  const canvas = document.createElement("canvas");
  canvas.width = 320;
  canvas.height = 180;
  canvas.style.cssText = "position:fixed;left:-10000px;top:-10000px;width:320px;height:180px";
  document.body.append(canvas);
  const gl = canvas.getContext("webgl2", {
    antialias: false,
    alpha: false,
    powerPreference: "high-performance",
  });
  const warnings = [pointerAdvisory(), widthAdvisory()].filter((x): x is CapabilityMessage => x !== null);
  const desktop = warnings.length === 0;
  if (!gl) {
    canvas.remove();
    return { webgl2: false, warmupFps: 0, renderer: "unavailable", desktop, passed: false, warnings,
      reason: { template: "浏览器未提供 WebGL2，无法运行 3D 交互。请换用较新的 Chrome 或 Edge，或在显卡设置里开启硬件加速。", values: [] } };
  }
  const renderer = rendererName(gl);
  // A software renderer is an advisory, not a verdict: the name is a proxy, it is unavailable
  // when the debug extension is blocked, and the frame-rate floor below is the direct test.
  if (/swiftshader|llvmpipe/i.test(renderer)) {
    warnings.push({ template: "检测到软件渲染（无显卡加速），操作可能很卡。建议换一台带独立显卡的电脑。", values: [] });
  }

  const module = await getMujoco();
  const vfs = new module.MjVFS();
  const xml = `<mujoco model="warmup"><option timestep="${TIMESTEP}"/><worldbody><geom type="plane" size="2 2 .1"/><body pos="0 0 .3"><freejoint/><geom type="box" size=".18 .18 .18"/></body></worldbody></mujoco>`;
  const model = module.MjModel.from_xml_string(xml, vfs);
  const data = new module.MjData(model);
  const threeRenderer = new THREE.WebGLRenderer({ canvas, context: gl, antialias: false });
  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(40, canvas.width / canvas.height, 0.01, 20);
  camera.position.set(1.4, 1.1, 1.4);
  camera.lookAt(new THREE.Vector3(0, 0, 0));
  scene.add(new THREE.HemisphereLight(0xffffff, 0x667060, 2));
  const cubeGeometry = new THREE.BoxGeometry(0.36, 0.36, 0.36);
  const cubeMaterial = new THREE.MeshStandardMaterial({ color: 0x2f7d68, flatShading: true });
  const cube = new THREE.Mesh(cubeGeometry, cubeMaterial);
  scene.add(cube);

  let measuredFrames = 0;
  let measureStart = 0;
  try {
    const start = performance.now();
    await new Promise<void>((resolve) => {
      const frame = (now: number): void => {
        for (let step = 0; step < STEPS_PER_FRAME; step += 1) module.mj_step(model, data);
        cube.rotation.y += 0.02;
        cube.rotation.x += 0.007;
        threeRenderer.render(scene, camera);
        if (now - start >= DISCARD_MS) {
          if (measureStart === 0) measureStart = now;
          measuredFrames += 1;
        }
        if (now - start >= WARMUP_MS) resolve();
        else requestAnimationFrame(frame);
      };
      requestAnimationFrame(frame);
    });
  } finally {
    cubeGeometry.dispose();
    cubeMaterial.dispose();
    threeRenderer.dispose();
    data.delete();
    model.delete();
    vfs.delete();
    canvas.remove();
  }
  const measuredMs = Math.max(1, performance.now() - measureStart);
  const warmupFps = (measuredFrames * 1000) / measuredMs;
  // Real-time simulation needs this many frames per second. Falling short slows the physics
  // down; it does not stop the study, and the viewer already says so at runtime, so it is an
  // advisory rather than a refusal.
  const realtimeFps = protocol.sim.rtf_pass_threshold / (TIMESTEP * STEPS_PER_FRAME);
  const passed = true;   // only the WebGL2 check above can refuse a participant
  if (warmupFps < realtimeFps) {
    warnings.push({ template: "预热帧率 {0} fps，低于实时仿真所需的 {1} fps。仿真会比真实时间慢，请多给每个产物一些操作时间；关闭其他标签页和录屏通常能改善。",
      values: [warmupFps.toFixed(0), realtimeFps.toFixed(0)] });
  }
  return {
    webgl2: true,
    warmupFps: Number(warmupFps.toFixed(2)),
    renderer,
    desktop,
    passed,
    warnings,
    reason: null,
  };
}
